import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Table, Tag, Button, Statistic, DatePicker, Divider } from 'antd';
import { FileTextOutlined, ReloadOutlined } from '@ant-design/icons';
import PageTitle from '@/components/PageTitle';
import { useDispatch, useSelector } from 'react-redux';
import { getMonitoringMissingTreatmentSessions } from '@/redux/actions/monitoringActions';
import CustomerInfoColumn from '@/components/customers/CustomerInfoColumn';
import { ConvertCustomerScheduleStatus } from '@/utils/helpers';
import moment from 'moment';
import qs from 'qs';
import { useLocation, useHistory } from 'react-router-dom';

const { RangePicker } = DatePicker;

const title = 'Giám sát vận hành';
const routes = [
    { path: '/', breadcrumbName: 'Trang chủ' },
    { path: '#', breadcrumbName: title },
];

const renderPresenceTag = (value) => {
    if (value) {
        return <Tag color="green">Đã có</Tag>;
    }

    return <Tag color="red">Chưa có</Tag>;
};

const styles = {
    block: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    row: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 12,
        // padding: '8px 10px',
        // border: '1px solid #f0f0f0',
        // borderRadius: 8,
        // background: '#fafafa',
    },
    label: {
        color: '#595959',
        fontSize: 15,
        lineHeight: 1.5,
        flex: 1,
        minWidth: 0,
    },
    value: {
        color: '#262626',
        fontSize: 13,
        lineHeight: 1.5,
        fontWeight: 500,
        textAlign: 'right',
        wordBreak: 'break-word',
        flex: '0 0 auto',
        maxWidth: 150,
    },
    presenceTag: {
        marginRight: 0,
        minWidth: 64,
        textAlign: 'center',
        fontWeight: 500,
        border: 'none',
    },
};

const Monitoring = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();

    const [loading, setLoading] = useState(false);

    const query = useMemo(
        () => qs.parse(location.search, { ignoreQueryPrefix: true }),
        [location.search]
    );

    const { items, pagination } = useSelector(state => state.monitoringReducer.treatment_missing);

    const loadData = async ({
        page = 1,
        limit = pagination.perPage || 20
    } = {}) => {
        try {
            setLoading(true);
            await dispatch(getMonitoringMissingTreatmentSessions({
                page,
                limit,
                from: query.from,
                to: query.to
            }));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData({ page: 1 });
    }, [query.from, query.to]);

    const handleRefresh = () => {
        loadData();
    };

    const onChangeFilter = (value) => {
        const newQuery = { ...query };

        if (value && value.length === 2) {
            newQuery.from = value[0].format('YYYY-MM-DD');
            newQuery.to = value[1].format('YYYY-MM-DD');
        } else {
            delete newQuery.from;
            delete newQuery.to;
        }

        history.push({
            pathname: location.pathname,
            search: qs.stringify(newQuery),
        });
    };

    const columnsTreatment = [
        {
            title: 'Khách hàng',
            dataIndex: 'customer',
            width: 280,
            render: (_, record) => (
                <div style={styles.block}>
                    <div style={styles.row}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                                style={{
                                    fontSize: 13,
                                    color: '#8c8c8c',
                                    marginBottom: 6,
                                    lineHeight: 1.4,
                                }}
                            >
                                Khách hàng
                            </div>

                            <div style={{ lineHeight: 1.5 }}>
                                <CustomerInfoColumn
                                    record={record}
                                    nameField="customer"
                                    idField="customer_id"
                                    codeField="customer_code"
                                />
                            </div>
                        </div>
                    </div>

                    <div style={styles.row}>
                        <span style={styles.label}>Lịch hẹn gần nhất</span>

                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-end',
                                gap: 4,
                                textAlign: 'right',
                                maxWidth: 160,
                                flex: '0 0 auto',
                            }}
                        >
                            <div
                                style={{
                                    color: record.scheduled_at ? '#262626' : '#bfbfbf',
                                    fontSize: 13,
                                    fontWeight: 500,
                                    lineHeight: 1.4,
                                    wordBreak: 'break-word',
                                }}
                            >
                                {record.scheduled_at || 'Chưa có lịch hẹn'}
                            </div>

                            {record.schedule_status ? (
                                <div>{ConvertCustomerScheduleStatus(record.schedule_status)}</div>
                            ) : (
                                <span style={{ color: '#bfbfbf', fontSize: 12 }}>
                                    Chưa có trạng thái
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            ),
        },
        {
            title: 'Bác sĩ/phụ tá',
            dataIndex: 'doctor',
            width: 250,
            render: (v, record) => {
                const doctor = record.doctor;
                const assistant = record.assistant;

                return (
                    <div style={styles.block}>
                        <div style={styles.row}>
                            <span style={styles.label}>Bác sĩ</span>
                            <span style={{ ...styles.value, color: doctor ? '#262626' : '#9d9d9dff' }}>
                                {doctor ? doctor : 'Chưa phân bác sĩ'}
                            </span>
                        </div>
                        <div style={styles.row}>
                            <span style={styles.label}>Phụ tá</span>
                            <span style={{ ...styles.value, color: assistant ? '#262626' : '#9d9d9dff' }}>
                                {assistant ? assistant : 'Chưa phân phụ tá'}
                            </span>
                        </div>
                    </div>
                );
            }
        },
        {
            title: 'Hồ sơ khách hàng',
            dataIndex: 'customer_profile',
            width: 250,
            render: (v, record) => {
                const has_birthday = record.has_birthday;
                const has_address = record.has_address;
                const has_phone = record.has_phone;

                return (
                    <div style={styles.block}>
                        <div style={styles.row}>
                            <span style={styles.label}>Ngày sinh</span>
                            <span>{React.cloneElement(renderPresenceTag(has_birthday), { style: styles.presenceTag })}</span>
                        </div>
                        <div style={styles.row}>
                            <span style={styles.label}>Địa chỉ</span>
                            <span>{React.cloneElement(renderPresenceTag(has_address), { style: styles.presenceTag })}</span>
                        </div>
                        <div style={styles.row}>
                            <span style={styles.label}>Số điện thoại</span>
                            <span>{React.cloneElement(renderPresenceTag(has_phone), { style: styles.presenceTag })}</span>
                        </div>
                    </div>
                );
            }
        },
        {
            title: 'Hồ sơ điều trị',
            dataIndex: 'has_before_image',
            width: 300,
            render: (v, record) => {
                const has_before_image = record.has_before_image;
                const has_after_image = record.has_after_image;
                const has_before_xray = record.has_before_xray;
                const has_after_xray = record.has_after_xray;
                const status = record.status;
                return (
                    <div style={styles.block}>
                        <div style={styles.row}>
                            <span style={styles.label}>Ảnh trước điều trị</span>
                            <span>{React.cloneElement(renderPresenceTag(has_before_image), { style: styles.presenceTag })}</span>
                        </div>
                        <div style={styles.row}>
                            <span style={styles.label}>Ảnh sau điều trị</span>
                            <span>{React.cloneElement(renderPresenceTag(has_after_image), { style: styles.presenceTag })}</span>
                        </div>
                        <div style={styles.row}>
                            <span style={styles.label}>Phim X-quang trước điều trị</span>
                            <span>{React.cloneElement(renderPresenceTag(has_before_xray), { style: styles.presenceTag })}</span>
                        </div>
                        <div style={styles.row}>
                            <span style={styles.label}>Phiếu điều trị</span>
                            <span>{React.cloneElement(renderPresenceTag(status), { style: styles.presenceTag })}</span>
                        </div>
                        {/* <div style={styles.row}>
                            <span style={styles.label}>Phim X-quang sau điều trị</span>
                            <span>{React.cloneElement(renderPresenceTag(has_after_xray), { style: styles.presenceTag })}</span>
                        </div> */}
                    </div>
                );
            }
        },
        {
            title: 'Người cập nhật',
            dataIndex: 'updated_by_name',
            render: (_, record) => {
                if (!record?.updated_by_name) {
                    return (
                        <span style={{ color: '#9d9d9dff' }}>
                            Chưa xác định
                        </span>
                    );
                }

                return (
                    <div style={{ lineHeight: 1.4 }}>
                        <div style={{ fontWeight: 500 }}>
                            {record.updated_by_name}
                        </div>

                        {record.updated_at ? (
                            <div style={{ color: '#8c8c8c', fontSize: 12 }}>
                                {record.updated_at}
                            </div>
                        ) : null}
                    </div>
                );
            }
        }
    ];

    return (
        <div>
            <PageTitle routes={routes} title={title} />

            <div className='mt-3'>
                <Row gutter={16} style={{ marginBottom: 12 }}>
                    <Col>
                        <RangePicker
                            format="DD/MM/YYYY"
                            placeholder={['Từ ngày', 'đến ngày']}
                            value={[
                                query.from ? moment(query.from) : moment(),
                                query.to ? moment(query.to) : moment()
                            ]}
                            onChange={onChangeFilter}
                        />
                        <Divider type="vertical" />
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={handleRefresh}
                            loading={loading}
                        >
                            Refresh
                        </Button>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col xs={24} sm={12} md={6}>
                        <Card className="card-red">
                            <Statistic
                                title="Phiếu điều trị chưa nhập"
                                value={pagination.total}
                                prefix={<FileTextOutlined />}
                            />
                        </Card>
                    </Col>
                </Row>
                <div
                    style={{
                        padding: '10px 14px',
                        background: '#fffbe6',
                        border: '1px solid #ffe58f',
                        borderRadius: 6,
                        color: '#614700',
                        lineHeight: 1.6,
                        marginTop: 12
                    }}
                >
                    Các khách hàng dưới đây hiện chưa có phiếu điều trị và chưa cập nhật đầy đủ hồ sơ liên quan. Vui lòng bổ sung đầy đủ thông tin cho từng khách hàng để đảm bảo hệ thống có đủ dữ liệu phục vụ việc tính toán hoa hồng.
                    <br />
                    <b>Lưu ý:</b> Nếu dữ liệu không đầy đủ, hệ thống sẽ không thực hiện tính hoa hồng cho các trường hợp này.
                </div>
                <div className="mt-3">
                    <Table
                        size='small'
                        rowKey="schedule_id"
                        loading={loading}
                        columns={columnsTreatment}
                        dataSource={items}
                        scroll={{ x: 'max-content' }}
                        pagination={{
                            current: pagination.currentPage,
                            pageSize: pagination.perPage,
                            total: pagination.total,
                            showSizeChanger: true,
                            showTotal: total => `Tổng ${total} bản ghi`,
                            onChange: (page, pageSize) => {
                                loadData({ page, limit: pageSize });
                            },
                        }}
                        rowClassName={(_, index) =>
                            index % 2 === 0 ? 'table-row-even' : 'table-row-odd'
                        }
                    />
                </div>
            </div>
        </div>
    );
};

export default Monitoring;
